/**
 * Análise de produto (A174): o app conta o uso das pacientes — telas, botões,
 * bloqueios — e manda à API em lote. Números nossos, de 100% das pessoas; o
 * Contentsquare (`contentsquare.ts`) segue para o porquê, em amostra.
 *
 * A API decide quem conta (só paciente, fora equipe e contas de teste) e
 * recusa o que não está no catálogo (`EVENTOS_DE_USO`). Aqui só se junta e
 * envia: a cada ~10 s, e na hora em que o app vai para segundo plano (com
 * `keepalive`, para o envio sobreviver ao fechamento da aba).
 *
 * Sem sessão não há envio: a conta é o que identifica a pessoa (como hash,
 * no servidor).
 */
import { api, domainConfig, sessionStore } from '@eva/domain';
import { telaDoCaminho, type EventoDeUso } from '@eva/schemas';

interface Roteador {
  state: { location: { pathname: string; search: string } };
  subscribe: (fn: (estado: { location: { pathname: string; search: string } }) => void) => () => void;
}

interface EventoNaFila {
  evento: EventoDeUso;
  props: Record<string, string>;
  ocorridoEm: string;
}

const INTERVALO_MS = 10_000;
/** Depois de tanto tempo em segundo plano, voltar ao app é uma sessão nova. */
const NOVA_SESSAO_APOS_MS = 30 * 60 * 1000;
const MAX_NA_FILA = 200;

let fila: EventoNaFila[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let ultimaChaveDeTela: string | null = null;
let escondidoDesde: number | null = null;
let iniciado = false;
let sessaoContada = false;

const temSessao = () => !!sessionStore.readTokens().accessToken;

/** Última atividade, guardada no aparelho: recarregar a página não é sessão nova. */
const CHAVE_DA_ATIVIDADE = 'eva_uso_ultima_atividade';
function lerAtividade(): number {
  try {
    return Number(localStorage.getItem(CHAVE_DA_ATIVIDADE)) || 0;
  } catch {
    return 0;
  }
}
function marcarAtividade() {
  try {
    localStorage.setItem(CHAVE_DA_ATIVIDADE, String(Date.now()));
  } catch {
    /* sem armazenamento: cada abertura conta como sessão */
  }
}

/**
 * Conta a sessão uma vez, na primeira tela com login (quem abre o app
 * deslogada e entra conta ao entrar), e só se ela estava parada há mais de
 * 30 minutos — recarregar a página no meio do uso não é sessão nova.
 */
function contarSessaoSePreciso() {
  if (sessaoContada || !temSessao()) return;
  sessaoContada = true;
  if (Date.now() - lerAtividade() > NOVA_SESSAO_APOS_MS) registrarUso('sessao_iniciada');
}

/** Registra um evento de uso. Nunca lança; sem sessão, descarta. */
export function registrarUso(evento: EventoDeUso, props: Record<string, string | null | undefined> = {}): void {
  try {
    if (!temSessao()) return;
    const limpas: Record<string, string> = {};
    for (const [k, v] of Object.entries(props)) if (v !== undefined && v !== null && v !== '') limpas[k] = String(v);
    fila.push({ evento, props: limpas, ocorridoEm: new Date().toISOString() });
    marcarAtividade();
    if (fila.length > MAX_NA_FILA) fila = fila.slice(-MAX_NA_FILA);
  } catch {
    /* análise nunca atrapalha o app */
  }
}

/**
 * O token ainda vale (com folga de 30 s)? O envio de saída (`keepalive`) não
 * tem como renovar o login: com o token vencido, a API recusa (401) e o lote
 * se perdia — 3 de 12 envios nas primeiras horas de produção (15/10/2026).
 */
function tokenValido(token: string | null): boolean {
  if (!token) return false;
  try {
    const carga = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof carga?.exp !== 'number' || carga.exp * 1000 - Date.now() > 30_000;
  } catch {
    return true; // formato desconhecido: tenta, como antes
  }
}

/** Eventos guardados no aparelho para a próxima abertura (saída com login vencido). */
const CHAVE_DOS_PENDENTES = 'eva_uso_pendentes';
/** De quem são: outra pessoa entrando no mesmo aparelho não herda o uso de quem saiu. */
const contaAtual = () => (sessionStore.readUser() as { id?: string } | null)?.id ?? null;
function guardarPendentes(lote: EventoNaFila[]) {
  try {
    const conta = contaAtual();
    const antes = JSON.parse(localStorage.getItem(CHAVE_DOS_PENDENTES) || 'null') as { conta: string | null; eventos: EventoNaFila[] } | null;
    const mesmos = antes && antes.conta === conta ? antes.eventos : [];
    localStorage.setItem(CHAVE_DOS_PENDENTES, JSON.stringify({ conta, eventos: [...mesmos, ...lote].slice(-MAX_NA_FILA) }));
  } catch {
    /* sem armazenamento: perde, como antes */
  }
}
function retomarPendentes() {
  try {
    const guardados = JSON.parse(localStorage.getItem(CHAVE_DOS_PENDENTES) || 'null') as { conta: string | null; eventos: EventoNaFila[] } | null;
    // Primeiro de volta à fila, depois apaga: se apagar falhar, o pior é mandar de novo, não perder.
    if (guardados && guardados.conta === contaAtual() && Array.isArray(guardados.eventos)) fila = [...guardados.eventos, ...fila].slice(-MAX_NA_FILA);
    localStorage.removeItem(CHAVE_DOS_PENDENTES);
  } catch {
    /* nada guardado */
  }
}

async function enviar(urgente = false): Promise<void> {
  if (!fila.length || !temSessao()) return;
  const lote = fila.splice(0, 50);
  if (urgente && !tokenValido(sessionStore.readTokens().accessToken)) {
    // Saindo com o login vencido: guarda, e o envio normal da próxima abertura renova e manda.
    guardarPendentes([...lote, ...fila.splice(0)]);
    return;
  }
  try {
    if (urgente) {
      // Saindo do app: fetch com keepalive (o axios não sobrevive ao fechamento).
      const token = sessionStore.readTokens().accessToken;
      await fetch(`${domainConfig().apiUrl}/api/analise/eventos`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ eventos: lote }),
      });
    } else {
      await api.post('/api/analise/eventos', { eventos: lote });
    }
  } catch {
    // Falhou: devolve à fila, uma vez — o próximo envio tenta de novo.
    if (!urgente) fila = [...lote, ...fila].slice(-MAX_NA_FILA);
  }
}

function aoMudarDeTela(caminho: string, busca: string) {
  const t = telaDoCaminho(caminho, busca);
  if (!t) return;
  contarSessaoSePreciso();
  const chave = `${t.tela}:${t.id ?? ''}`;
  if (chave === ultimaChaveDeTela) return;
  ultimaChaveDeTela = chave;
  registrarUso('tela_vista', { tela: t.tela, id: t.id });
}

/** Liga a contagem ao roteador. Chamado uma vez, na entrada do app. */
export function iniciarUso(r: Roteador): void {
  if (iniciado || typeof window === 'undefined') return;
  iniciado = true;
  // O que ficou guardado na última saída (login vencido) vai no primeiro envio, que renova o login.
  if (temSessao()) retomarPendentes();
  aoMudarDeTela(r.state.location.pathname, r.state.location.search);
  r.subscribe((estado) => aoMudarDeTela(estado.location.pathname, estado.location.search));
  timer = setInterval(() => void enviar(), INTERVALO_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      escondidoDesde = Date.now();
      void enviar(true);
    } else if (escondidoDesde && Date.now() - escondidoDesde > NOVA_SESSAO_APOS_MS) {
      // Voltou depois de muito tempo em segundo plano: sessão nova.
      escondidoDesde = null;
      ultimaChaveDeTela = null;
      sessaoContada = false;
      aoMudarDeTela(r.state.location.pathname, r.state.location.search);
    } else {
      escondidoDesde = null;
    }
  });
  window.addEventListener('pagehide', () => void enviar(true));
}

/** Só para testes. */
export function _filaDeUso(): EventoNaFila[] {
  return fila;
}
export function _zerarUso(): void {
  fila = [];
  ultimaChaveDeTela = null;
  escondidoDesde = null;
  iniciado = false;
  sessaoContada = false;
  if (timer) clearInterval(timer);
  timer = null;
}
